import { useRouteQuery } from '@/common/route/useRouteQuery';
import { pageSize } from '@/common/utils/helpers';
import { useBaseListView } from '@/common/view/useBaseListView';
import { useCrudActions } from '@/common/view/useCrudActions';
import { TableAction, TableColumn, TableColumnType } from '@/components/table/types';
import { ArenaStatusList } from '@/modules/arena/item/model/mixins';
import { ROUTE_NAME_ARENA } from '@/modules/arena/router';
import { ArenaTicketTypeList } from '@/modules/arena/ticket/model/mixins';
import { useGameMatchList } from '@/modules/game/match/model/list';
import { ROUTE_NAME_GAME } from '@/modules/game/router';
import { arenaService } from '@/services/arena';
import { onMounted } from 'vue';

export function useArenaList() {
    const { confirmAndDisable, confirmAndEnable, confirmAndRemove } = useCrudActions();
    const { fetchAllMatches, gameMatchCache } = useGameMatchList();

    const { query, updateQueryToRoute } = useRouteQuery({
        definition: {
            offset: { type: 'number', default: 0 },
            limit: { type: 'number', default: pageSize }
        },
        onChange: () => fetchList()
    });

    const { fetchList, list, total } = useBaseListView({
        request: arenaService.item.list,
        paramsBuilder: () => ({ ...query }),
        hooks: {
            loadInitialData: fetchAllMatches,
            onPageChange: offset => (query.offset = offset),
            onSuccess: updateQueryToRoute,
            transformResponse: (list: any[]) =>
                list.map(item => ({
                    ...item,
                    matchName: gameMatchCache.value.get(item.matchId)?.label ?? ''
                }))
        }
    });

    onMounted(fetchList);

    const columns: TableColumn[] = [
        { type: TableColumnType.Text, label: 'ID', key: 'id' },
        { type: TableColumnType.Text, label: 'Name', key: 'name' },
        {
            type: TableColumnType.RouterLink,
            label: 'Match',
            key: 'matchName',
            to: row => ({ name: ROUTE_NAME_GAME.MATCH.DETAIL, params: { id: Number(row.matchId) } })
        },
        { type: TableColumnType.Text, label: 'Price', key: 'price' },
        { type: TableColumnType.Option, label: 'Ticket Type', key: 'ticketType', options: ArenaTicketTypeList },
        { type: TableColumnType.Option, label: 'Status', key: 'status', options: ArenaStatusList },
        { type: TableColumnType.DateTime, label: 'Publish Time', key: 'publishTime' },
        { type: TableColumnType.DateTime, label: 'Unpublish Time', key: 'unpublishTime' },
        { type: TableColumnType.Actions, label: 'Actions' }
    ];

    const actions: TableAction[] = [
        {
            props: row => ({
                icon: 'fa-edit',
                theme: 'info',
                tooltip: 'Edit',
                to: { name: ROUTE_NAME_ARENA.ITEM.EDIT, params: { id: row.id } }
            }),
            canShow: row => row.canEdit,
            group: 0
        },
        {
            props: row => ({
                icon: 'fa-copy',
                theme: 'info',
                tooltip: 'Clone',
                to: { name: ROUTE_NAME_ARENA.ITEM.CLONE, params: { id: row.id } }
            }),
            canShow: row => row.canClone,
            group: 0
        },
        {
            props: row => ({
                icon: 'fa-eye',
                theme: 'info',
                tooltip: 'Detail',
                to: { name: ROUTE_NAME_ARENA.ITEM.DETAIL, params: { id: row.id } }
            }),
            group: 0
        },
        {
            props: row => ({
                icon: 'fa-medal',
                theme: 'info',
                tooltip: 'Show medal',
                to: { name: ROUTE_NAME_ARENA.ITEM.MEDAL, params: { id: row.id } }
            }),
            group: 1
        },
        {
            props: row => ({
                icon: 'fa-play',
                theme: 'success',
                tooltip: 'Enable',
                onclick: confirmAndEnable({
                    request: () => arenaService.item.enable(row.id),
                    onSuccess: () => fetchList()
                })
            }),
            canShow: row => row.canEnable,
            group: 2
        },
        {
            props: row => ({
                icon: 'fa-stop',
                theme: 'warning',
                tooltip: 'Disable',
                onclick: confirmAndDisable({
                    request: () => arenaService.item.disable(row.id),
                    onSuccess: () => fetchList()
                })
            }),
            canShow: row => row.canDisable,
            group: 3
        },
        {
            props: row => ({
                icon: 'fa-trash-alt',
                theme: 'danger',
                tooltip: 'Delete',
                disabled: !row.canDelete,
                onClick: confirmAndRemove({
                    request: () => arenaService.item.remove(row.id),
                    onSuccess: () => fetchList()
                })
            })
        }
    ];

    const table = {
        columns,
        actions
    };

    return { list, total, query, fetchList, table };
}
